import React from 'react';
import _ from 'lodash';

import {
  LoadingOverlay,
  Table,
  errorUtil,
  modalUtil
} from 'iron-forge-react-modules';
import {
  PermissionWall,
  permissionUtil,
  tableUtil
} from 'modules';
import {
  Button,
  Fab,
  styled
} from '@mui/material';

import { Subject } from 'rxjs';
import * as EditCachedOrderModal from './EditCachedOrderModal/EditCachedOrderModal';
import * as sharedTypes from '../types';
import * as types from './types';
import * as dataService from './dataService';



const TableComponent = Table.genTableComponent<types.IColumnId>({});

const FloatingFab = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(4),
  left: 'calc(50vw)',
  transform: 'translateX(-50%)'
}));

const __obsDeselect = new Subject<void>();



export interface IProps {
  filter: sharedTypes.IFilter;
}

export const FC: React.FC<IProps> = ({ filter }) => {
  const obsDeselectAll = React.useMemo(() => __obsDeselect.asObservable(), []);
  const { modalType2TargetId, setModalIsOpen } = modalUtil.useModalTrackers(['editOrder']);
  const {
    fnQueryPage,
    queryTuple,
    pageInfo,
    fnQuery
  } = dataService.useQuery(filter);
  const [reviewCachedOrders, reviewOrdersTuple] = dataService.useReviewCachedOrders(filter);

  tableUtil.useQueryOnFilterChange({
    filter,
    fnQuery
  });

  const [hasPermission, hasPermissionsLoading] = permissionUtil.usePermissions({});
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);



  const reviewOrders = React.useCallback(
    async (cachedOrderIds: string[]) => {
      const confirmed = window.confirm(`Are you sure you want to archive ${cachedOrderIds.length} orders?`);
      if (confirmed) {
        await errorUtil.errorWrap(async () => {
          await reviewCachedOrders(cachedOrderIds);
          __obsDeselect.next();
          queryTuple.refetch?.();
        }, {
          DOES_NOT_HAVE_PERMISSION_FOR_STORE: "You do not have permission to review orders for stores for all selected orders.",
          REVIEWS_EXIST_FOR_ORDERS: "A review already exists for one or more selected orders."
        });
      }
    },
    [
      queryTuple,
      reviewCachedOrders
    ]
  );

  const handleClickPopoverOption = React.useCallback(
    async (cachedOrderIds: string[], optionId: string) => {
      if (optionId === 'reviewCachedOrders' && !_.isEmpty(cachedOrderIds)) {
        await reviewOrders(cachedOrderIds);
      }
    },
    [reviewOrders]
  );



  const handleChangeSelections = React.useCallback(
    (rowId2IsSelected: Record<string, boolean>) => {
      setSelectedIds(_.keys(_.pickBy(rowId2IsSelected)));
    },
    []
  );



  const rows = React.useMemo(
    () => _.map(queryTuple.data.cachedOrders, (order): types.IRow => ({
      id: order.id,
      columnId2Value: {
        orderDate: {
          type: 'string',
          label: order.createdTimestamp.toFormat('yyyy-MM-dd @ HH:mm')
        },
        orderNumber: {
          type: 'string',
          label: order.orderNumber
        },
        validationErrors: {
          type: 'string',
          label: order.validationErrors.join(', ')
        },
        store: {
          type: 'string',
          label: order.storeName
        },
        actions: {
          type: 'component',
          sortableValue: '',
          label: !_.isEmpty(order.validationErrors) ? (
            <Button onClick={() => setModalIsOpen('editOrder', order.id)}>
              Edit
            </Button>
          ) : (
            <div />
          )
        }
      }
    })),
    [
      queryTuple.data.cachedOrders,
      setModalIsOpen
    ]
  );

  const hasPermissionToReviewOrders = React.useMemo(
    () => hasPermission(['salesOrder_reviewCachedOrder']),
    [hasPermission]
  );

  const handleClickArchiveOrders = React.useCallback(
    async () => {
      await reviewOrders(selectedIds);
    },
    [
      reviewOrders,
      selectedIds
    ]
  );

  const loading = React.useMemo(
    () => queryTuple.loading || reviewOrdersTuple.loading || hasPermissionsLoading,
    [
      hasPermissionsLoading,
      queryTuple.loading,
      reviewOrdersTuple.loading
    ]
  );

  return (
    <>
      {selectedIds.length > 0 && (
        <PermissionWall.FC requiresPermissions={['salesOrder_reviewCachedOrder']}>
          <FloatingFab
            color="primary"
            disabled={loading || selectedIds.length === 0}
            variant="extended"
            onClick={handleClickArchiveOrders}>
            {__getArchiveButtonText(selectedIds.length)}
          </FloatingFab>
        </PermissionWall.FC>
      )}
      <TableComponent
        rows={rows}
        multiSelectOptions={hasPermissionToReviewOrders ? {
          obsDeselectAll,
          onChangeSelections: handleChangeSelections,
          onClickPopoverOption: handleClickPopoverOption,
          popoverOptions: [
            {
              id: 'reviewCachedOrders',
              label: "Review Orders"
            }
          ]
        } : undefined}
        paginationOptions={Table.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
        initSortOptions={{
          columnId: 'orderDate',
          direction: 'descending'
        }}
        columns={[
          {
            id: 'orderNumber',
            label: "Order #"
          },
          {
            id: 'orderDate',
            label: "Created"
          },
          {
            id: 'store',
            label: "Store"
          },
          {
            id: 'validationErrors',
            label: "Validation Errors"
          },
          {
            id: 'actions',
            label: "Actions"
          }
        ]} />

      <LoadingOverlay.FC active={loading} />

      {modalType2TargetId.editOrder && (
        <EditCachedOrderModal.FC
          cachedOrderId={modalType2TargetId.editOrder}
          onClose={() => setModalIsOpen('editOrder', false)} />
      )}
    </>
  );
};



function __getArchiveButtonText(count: number) {
  if (count === 0) {
    return "No orders selected";
  } else if (count === 1) {
    return "Archive 1 order";
  } else {
    return `Archive ${count} orders`;
  }
}
